import { useEffect } from 'react';

import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { getMeCenterSubscription } from '@api/selectors/subscription';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import { storeSubscriptionTypes } from '@cont/StoreListing/constants';
import usePlusIconicSubscribed from '@hooks/usePlusIconicSubscribed';
import { hasActiveTrialSubscriptionToGoal } from '@models/Me';
import { PrimeMode, UserPlatformPlan } from '@stores/prime-quota';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { readCookie } from '@utils/cookies';

const STORE_TEST_OR_NOTES_TYPES = [
  storeSubscriptionTypes.TEST_SERIES,
  storeSubscriptionTypes.NOTES
];

export const hasActiveTestOrNotesStoreSubscription = (me = {}) => {
  const timeline =
    me.userSubscriptionTimeline ?? me.latestUserSubscriptionsTimeline ?? [];

  return timeline.some((entry) => {
    const contentTypeId = entry?.contentTypeId ?? entry?.content_type_id;
    const timeRemaining = entry?.time_remaining ?? entry?.timeRemaining ?? 0;

    return (
      STORE_TEST_OR_NOTES_TYPES.includes(contentTypeId) && timeRemaining > 0
    );
  });
};

const useTestListingLockState = (goalUid) => {
  const { myInfo } = useMyInfo();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { topology } = useTopology(goalUid);
  const isPrimeEnabled = getIsPrimeEnabled(topology);

  const fetchQuotaDetails = usePrimeQuotaStore(
    (state) => state.fetchQuotaDetails
  );
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);
  const userPlatformPlan = usePrimeQuotaStore(
    (state) => state.userPlatformPlan
  );

  const isPlusOrIconicUser = usePlusIconicSubscribed({ goalUID: goalUid });
  const isTrialUser = hasActiveTrialSubscriptionToGoal(myInfo ?? {}, goalUid);
  const isFreeUserOnGoal = !isPlusOrIconicUser && !isTrialUser;
  const isOfflineCentreLearner = getMeCenterSubscription(myInfo ?? {});

  useEffect(() => {
    if (!goalUid || !isLoggedIn || !isPrimeEnabled || isPlusOrIconicUser) {
      return;
    }

    fetchQuotaDetails({
      goalUID: goalUid,
      accessToken: readCookie('accessToken')
    });
  }, [
    fetchQuotaDetails,
    goalUid,
    isLoggedIn,
    isPlusOrIconicUser,
    isPrimeEnabled
  ]);

  const testQuota = getQuotaForMode(PrimeMode.TEST);
  const isPrimeQuotaExhausted = testQuota.isQuotaExhausted;
  const hasPrimePaidPlan = userPlatformPlan === UserPlatformPlan.PAID;
  const showPrimeStickyBanner =
    isLoggedIn &&
    isPrimeEnabled &&
    isFreeUserOnGoal &&
    !hasPrimePaidPlan &&
    !isOfflineCentreLearner;

  return {
    isPlusOrIconicUser,
    isFreeUserOnGoal,
    isOfflineCentreLearner,
    hasActiveTestOrNotesStoreSubscription:
      hasActiveTestOrNotesStoreSubscription(myInfo ?? {}),
    isPrimeEnabled,
    isPrimeQuotaExhausted,
    showPrimeStickyBanner,
    testQuotaLeft: testQuota.left,
    testQuotaTotal: testQuota.total
  };
};

export default useTestListingLockState;
